import { useEffect, useState, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { teacherStudentList } from "@/lib/api";
import { Users, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from "lucide-react";

/* ── Status badge ────────────────────────────────────────────────────────── */
const STATUS_STYLES = {
  "Completed":   "bg-emerald-100 text-emerald-800 border-emerald-200",
  "In Progress": "bg-amber-100  text-amber-800  border-amber-200",
  "Gone Quiet":  "bg-red-100    text-red-800    border-red-200",
  "Not Started": "bg-slate-100  text-slate-600  border-slate-200",
};

const STATUS_DOT = {
  "Completed":   "bg-emerald-500",
  "In Progress": "bg-amber-500",
  "Gone Quiet":  "bg-red-500",
  "Not Started": "bg-slate-400",
};

/* Status sort order so default "by status" puts most urgent first */
const STATUS_ORDER = { "Not Started": 0, "Gone Quiet": 1, "In Progress": 2, "Completed": 3 };

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES["Not Started"];
  const dot = STATUS_DOT[status] || STATUS_DOT["Not Started"];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
      {status}
    </span>
  );
}

/* ── Sort header button ──────────────────────────────────────────────────── */
function SortBtn({ label, field, sort, setSort, testId }) {
  const active = sort.field === field;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      data-testid={testId}
      onClick={() =>
        setSort(prev =>
          prev.field === field
            ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { field, dir: "asc" }
        )
      }
      className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide hover:text-slate-900 transition select-none ${active ? "text-slate-900" : "text-slate-400"}`}
    >
      {label}
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
    </button>
  );
}

/* ── Main panel ──────────────────────────────────────────────────────────── */
export default function StudentListPanel({ open, onClose, teacherId, grade, subject }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isSubjectCtx = Boolean(subject);

  const [sort, setSort] = useState(() =>
    isSubjectCtx ? { field: "status", dir: "asc" } : { field: "name", dir: "asc" }
  );

  // Reset sort when context changes
  useEffect(() => {
    setSort(isSubjectCtx ? { field: "status", dir: "asc" } : { field: "name", dir: "asc" });
  }, [isSubjectCtx, subject, grade]);

  // Fetch on open
  useEffect(() => {
    if (!open || !teacherId || grade == null) return;
    setLoading(true);
    setError(null);
    setData(null);
    teacherStudentList(teacherId, grade, subject || null)
      .then(setData)
      .catch(() => setError("Could not load student list."))
      .finally(() => setLoading(false));
  }, [open, teacherId, grade, subject]);

  const sorted = useMemo(() => {
    if (!data?.students) return [];
    return [...data.students].sort((a, b) => {
      let cmp = 0;
      if (sort.field === "status") {
        cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
      } else if (sort.field === "name") {
        cmp = (a.name || "").localeCompare(b.name || "");
      } else if (sort.field === "last_seen") {
        // Treat "Never" as oldest
        const scoreA = a.last_seen === "Never" ? 9999 : (parseInt(a.last_seen) || 0);
        const scoreB = b.last_seen === "Never" ? 9999 : (parseInt(b.last_seen) || 0);
        cmp = scoreA - scoreB;
      } else if (sort.field === "sub_topic") {
        cmp = (a.current_sub_topic || "").localeCompare(b.current_sub_topic || "");
      } else if (sort.field === "subject") {
        cmp = (a.last_active_subject || "").localeCompare(b.last_active_subject || "");
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [data, sort]);

  const panelTitle = isSubjectCtx
    ? `Grade ${grade} · ${subject} — Students`
    : `Grade ${grade} — All Students`;

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="student-list-panel"
      >
        {/* Header */}
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="font-teacher text-base leading-snug text-slate-900">
                {panelTitle}
              </SheetTitle>
              {data && (
                <p className="text-xs text-slate-500 mt-0.5" data-testid="student-panel-count">
                  {data.total} student{data.total === 1 ? "" : "s"}
                </p>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-400" data-testid="student-panel-loading">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading…
            </div>
          )}

          {error && (
            <div className="text-center py-12 text-red-600 text-sm" data-testid="student-panel-error">
              {error}
            </div>
          )}

          {!loading && !error && data && data.total === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2" data-testid="student-panel-empty">
              <Users className="w-10 h-10 opacity-30" />
              <p className="text-sm font-medium">No students in this grade yet</p>
            </div>
          )}

          {!loading && !error && data && data.total > 0 && (
            <>
              {/* Sort bar */}
              <div className="flex items-center gap-3 mb-3 pb-2 border-b border-slate-100">
                <span className="text-xs text-slate-400 font-medium mr-1">Sort by</span>
                {isSubjectCtx ? (
                  <>
                    <SortBtn label="Status"    field="status"    sort={sort} setSort={setSort} testId="sort-status" />
                    <SortBtn label="Name"      field="name"      sort={sort} setSort={setSort} testId="sort-name" />
                    <SortBtn label="Last Seen" field="last_seen" sort={sort} setSort={setSort} testId="sort-last-seen" />
                  </>
                ) : (
                  <>
                    <SortBtn label="Name"      field="name"      sort={sort} setSort={setSort} testId="sort-name" />
                    <SortBtn label="Last Seen" field="last_seen" sort={sort} setSort={setSort} testId="sort-last-seen" />
                  </>
                )}
              </div>

              {/* Student rows */}
              <div className="space-y-2" data-testid="student-list">
                {isSubjectCtx ? (
                  sorted.map(s => (
                    <div
                      key={s.learner_id}
                      data-testid={`student-row-${s.learner_id}`}
                      className="rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition px-4 py-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* Avatar + name */}
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-teacher font-bold text-slate-700 text-sm flex-shrink-0">
                            {(s.name || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-teacher font-semibold text-slate-900 text-sm leading-tight truncate" data-testid={`student-name-${s.learner_id}`}>
                              {s.name}
                            </p>
                            {s.current_sub_topic && (
                              <p className="text-xs text-slate-500 truncate mt-0.5" data-testid={`student-subtopic-${s.learner_id}`}>
                                {s.current_sub_topic}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Status + last seen */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <StatusBadge status={s.status} />
                          <span className="text-xs text-slate-400" data-testid={`student-lastseen-${s.learner_id}`}>
                            {s.last_seen}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  sorted.map(s => (
                    <div
                      key={s.learner_id}
                      data-testid={`student-row-${s.learner_id}`}
                      className="rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition px-4 py-3 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-teacher font-bold text-slate-700 text-sm flex-shrink-0">
                        {(s.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-teacher font-semibold text-slate-900 text-sm leading-tight truncate" data-testid={`student-name-${s.learner_id}`}>
                          {s.name}
                        </p>
                        {s.last_active_subject && (
                          <p className="text-xs text-slate-500 truncate mt-0.5" data-testid={`student-subject-${s.learner_id}`}>
                            {s.last_active_subject}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs text-slate-400" data-testid={`student-lastseen-${s.learner_id}`}>
                          {s.last_seen}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
